import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ensureDefaultLights } from 'entangled-worlds-orbiters-shared/core';
import { applyCameraInsetOffset } from '../ui/viewport.js';

/**
 * In a shared realm every voice is drawn by ONE compositor loop, so the frame rate is the realm's,
 * not any one voice's — and N voices each painting their own overlay would be N stacked readouts of
 * the same number. The first voice to boot takes the measurement, and the rest stay quiet.
 */
let sharedFrameStatsOwner = null;

/**
 * Centralises Three.js scene, renderer, and camera management for Orbiters.
 * This controller bootstraps the shared Entangled Worlds renderer while
 * keeping hooks for Orbiters-specific overlays (e.g. oscillation rings).
 */
export class WorldSceneController {
  constructor(canvas, options = {}) {
    if (!canvas) {
      throw new Error('[WorldSceneController] canvas element is required');
    }

    this.canvas = canvas;
    // 'full' draws the world as configured; 'off' draws nothing at all — no renderer, no WebGL
    // context, no loop. A simplified scene was the first answer here and measurement refuted it: on
    // the machine the mode exists for, one plain sphere and basic moons at pixel ratio 1 still ran
    // below the frame cap, and backgrounding the tab — the browser doing "no rendering" for free —
    // cleaned the audio instantly. The cost is not what we draw, it is that we draw. So the audio-first
    // mode does not draw.
    //
    // It is a RUNTIME switch, independent of graphics quality, and it never touches the world's own
    // config or anything on the audio side.
    this._sceneProfile = options.sceneProfile === 'off' ? 'off' : 'full';
    this.performanceProfile = this.#resolvePerformanceProfile(options.performanceProfile);
    this._showFpsOverlay = Boolean(options.showFpsOverlay);
    this.scene = new THREE.Scene();
    // In the multi-orbiter realm a single compositor owns ONE renderer + ONE rAF loop and
    // renders each voice's scene into a scissor-rect viewport. A voice passes that `sharedRenderer`
    // here: this controller then borrows it (never creates/sizes/disposes it) and skips its own loop —
    // the compositor calls `renderViewport()` each frame. No `sharedRenderer` → single-orbiter is
    // byte-identical (owns its renderer, its rAF loop, its resize binding, its global slots).
    this._sharedRenderer = options.sharedRenderer || null;
    // Whether this voice belongs to a realm, which is a fact about the CONSTRUCTION PATH and stays
    // true whether or not a renderer was handed over. It decides who owns the renderer's lifecycle:
    // a realm's compositor is one renderer for every voice at once, so the realm switches it and the
    // voice only ever switches its own scene contents.
    this._isRealmVoice = Boolean(options.realmVoice ?? options.sharedRenderer);
    // This voice's ONE camera-input surface — its DOM cell in a shared realm, or the app's
    // fullscreen canvas single-orbiter. Always set. The CameraController binds exactly this element (never
    // the shared realm canvas, which is render-only), so a pointer event can only ever be for THIS voice.
    this.inputElement = options.inputElement || null;
    // Ownership follows the CONSTRUCTION PATH, not whether a renderer happened to be handed over. A
    // realm voice booting with the 3D off is given none at all, and inferring from that would have
    // every tile decide it was a lone orbiter — binding its own resize to the shared canvas and
    // claiming the realm's window slots, N tiles deep, last writer wins.
    this._ownsRenderer = !this._isRealmVoice && !this._sharedRenderer;
    // With the 3D off nothing is drawn, so nothing is created: no context, not even an idle one, and
    // no borrowed one either. An idle context still holds its drawing buffer and still costs the
    // compositor a viewport per frame, which is the cost this mode exists to remove — so `renderer`
    // is genuinely absent here, and every path that draws checks for it.
    //
    // The scene and the camera stay. They are plain JS object graphs with nothing on the GPU, and
    // keeping them means every consumer of this controller — the camera binder, the world manager,
    // the effect-visuals bridge, play mode — holds the same reference in both modes and never has to
    // ask which one it is in. Turning the 3D back on refills them; it does not replace them.
    this.renderer = this.#is3dOff() ? null : this._ownsRenderer ? this.#createRenderer(canvas) : this._sharedRenderer;
    this.camera = this.#createCamera();
    this.controls = this.renderer ? this.#createControls() : null;
    this.overlayGroups = new Set();
    this.renderCallbacks = new Set();
    // An optional pass that re-draws this voice's frame through a shader (the grit
    // group crushes the picture). The controller owns the ONE draw call on both
    // paths, so this is the only place such a thing can live — and because the pass
    // renders into its own target and lands back inside this voice's scissor rect,
    // a sibling voice sharing the renderer is untouched.
    this._postPass = null;
    this._animationFrame = null;
    this._resizeObserver = null;
    // Last devicePixelRatio the renderer profile was applied for (see #applySize).
    this._lastAppliedDpr = null;
    this._renderActive = true;
    this._disposed = false;
    // Right-edge inset (px) reserved for the Orbiter Studio panel; 0 = full-bleed (play mode).
    this._viewportInsetRight = 0;
    this._fpsOverlayEl = null;
    this._lastFrameTimestamp = null;
    this._fpsSampleWindow = [];
    this._fpsSampleWindowSize = 90;
    this._fpsSampleTotal = 0; // running sum of the window (avoids a per-frame reduce)
    this._frameStats = null; // the object mutated in place each frame (also on window.__orbitersFrameStats)
    this._fpsOverlayLastPaintAt = 0;
    // The ONE frame clock, passed to every render callback (see addRenderCallback).
    // Mutated in place each frame — no per-frame allocation.
    this._frame = { nowSec: 0, dtSec: 0 };
    this._frameLastNowMs = null;

    ensureDefaultLights(this.scene);

    // Shared-renderer voices don't own the loop, the canvas size, or the singleton window slots — the
    // compositor does. They only contribute a scene+camera and a per-frame callback set.
    //
    // Frame stats are the exception, and they have to be: a room with four voices is exactly the case
    // worth measuring, and it is the case where nobody owns the renderer — so gating the readout on
    // ownership meant no measurement precisely where it matters most. One voice takes it for the realm.
    if (!this._ownsRenderer && !sharedFrameStatsOwner) {
      sharedFrameStatsOwner = this;
      this.#initFrameStats();
    }

    if (this._ownsRenderer) {
      // Stats are initialised even with the 3D off: the readout is how you confirm the mode is
      // genuinely stopped rather than merely painting nothing, so it has to exist in the mode it
      // reports on. It reports zeroes and stops updating, because nothing is drawing.
      this.#initFrameStats();
      this.#startLoop();
      this.#bindResize();

      // Let the React Studio shell reserve the right-edge panel region (mirrors EW's
      // `window.__EW_setEditViewport`). One controller is active at a time, so last-writer-wins is fine.
      if (typeof window !== 'undefined') {
        window.__orbitersSetViewportInset = (rightPx) => this.setViewportInset(rightPx);
        // ...and let it say "the canvas box just changed" after a CSS-driven resize (the mobile edit
        // sheet writes a reserve var). Nothing announces such a change, so without this the re-fit
        // waits on the ResizeObserver — and if that tick is late or never delivered, the drawing
        // buffer keeps the OLD shape while the box has the new one, and the orbiter renders stretched.
        window.__orbitersRefitViewport = () => this.#applySize();
      }
    }
  }

  /** Whether this controller is in the no-3D mode. The one place the profile string is compared. */
  #is3dOff() {
    return this._sceneProfile === 'off';
  }

  /** True while there is something to draw with. False in the no-3D mode and after dispose. */
  get has3d() {
    return Boolean(this.renderer) && !this._disposed;
  }

  /** Whether this voice is one of a realm's, whose renderer the realm owns rather than the voice. */
  get isRealmVoice() {
    return this._isRealmVoice;
  }

  #resolvePerformanceProfile(profile) {
    const defaults = {
      maxDevicePixelRatio: 1.5,
      antialias: true,
      shadowsEnabled: false,
    };

    if (!profile || typeof profile !== 'object') {
      return { ...defaults };
    }

    const resolveNumeric = (value, fallback) => {
      const number = Number(value);
      if (!Number.isFinite(number) || number <= 0) {
        return fallback;
      }
      return number;
    };

    return {
      maxDevicePixelRatio: resolveNumeric(profile.maxDevicePixelRatio, defaults.maxDevicePixelRatio),
      antialias: profile.antialias !== undefined ? Boolean(profile.antialias) : defaults.antialias,
      shadowsEnabled:
        profile.shadowsEnabled !== undefined ? Boolean(profile.shadowsEnabled) : defaults.shadowsEnabled,
    };
  }

  #applyRendererProfile(renderer) {
    if (!renderer) return;

    // The user's graphics quality, unmodified. The audio-first mode used to override these knobs
    // downward; it now removes the renderer instead, so there is nothing left for it to clamp and
    // the quality setting is the only thing that speaks here.
    const profile = this.performanceProfile;
    const dpr =
      typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
        ? window.devicePixelRatio
        : 1;
    const maxDpr = profile.maxDevicePixelRatio || 1.5;
    renderer.setPixelRatio(Math.min(dpr, maxDpr));
    renderer.shadowMap.enabled = Boolean(profile.shadowsEnabled);
    renderer.userData = renderer.userData || {};
    renderer.userData.performanceProfile = {
      ...(renderer.userData.performanceProfile || {}),
      maxDevicePixelRatio: maxDpr,
      antialias: Boolean(profile.antialias),
    };
  }

  #createRenderer(canvas) {
    const renderer = new THREE.WebGLRenderer({
      canvas,
      // The user's QUALITY setting. Antialias is a context-creation attribute, which is another
      // reason the audio-first mode destroys the context rather than degrading it: a mode that
      // downgraded this in place could never restore it, and the user would silently lose their
      // antialiasing for the rest of the session. Rebuilding gets it right by construction.
      antialias: Boolean(this.performanceProfile.antialias),
      alpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    this.#applyRendererProfile(renderer);
    renderer.setSize(canvas.clientWidth || window.innerWidth, canvas.clientHeight || window.innerHeight, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    return renderer;
  }

  #createCamera() {
    const width = this.canvas.clientWidth || window.innerWidth || 1;
    const height = this.canvas.clientHeight || window.innerHeight || 1;
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
    camera.position.set(0, 0, 2);
    return camera;
  }

  #createControls() {
    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    // disable all user interaction
    controls.enabled = false;
    controls.enableRotate = false;
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.enableDamping = false;
    // keep distances as-is (irrelevant when disabled)
    controls.minDistance = 0.5;
    controls.maxDistance = 10;
    return controls;
  }

  #bindResize() {
    this._onResize = () => this.#applySize();
    window.addEventListener('resize', this._onResize);
    // The canvas box is not always the window box: in edit mode the bottom sheet reserves room at the
    // foot of `.ratio-frame`, which resizes the canvas with no window event to hear. Observe the box
    // itself — one owner for "the canvas changed size", whatever moved it.
    if (typeof ResizeObserver === 'function') {
      this._resizeObserver = new ResizeObserver(() => this.#applySize());
      this._resizeObserver.observe(this.canvas);
    }
  }

  /**
   * Fit the renderer + camera to the canvas. For the Orbiter Studio panel we keep the canvas
   * FULL-WIDTH — so the live scene (starfield) renders BEHIND the frosted panel (the semi-transparent
   * look) — and shift the orbiter into the left region with a camera VIEW-OFFSET (`applyCameraInsetOffset`,
   * the same single source the resize handler uses). This never shrinks the canvas, so the WebGL buffer
   * can't squish (a `<canvas>` is a replaced element). With no inset this is the play-mode path (sizes to
   * the canvas client box, no offset) — unchanged.
   */
  #applySize() {
    // Nothing to fit with the 3D off. The camera is re-fitted when the renderer is rebuilt, so a
    // window resized while the mode was on is still correct on the way out.
    if (!this.renderer) return;
    const inset = this._viewportInsetRight || 0;
    // Canvas is always full-bleed (the stylesheet owns the box); we never confine it via CSS anymore.
    this.canvas.style.removeProperty('left');
    this.canvas.style.removeProperty('width');
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    if (!width || !height) return;

    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    applyCameraInsetOffset(this.camera, inset, width, height);
    this.camera.updateProjectionMatrix();
    // Re-clamp the pixel ratio only when the DISPLAY actually changed (dragging the window to a
    // second monitor fires a resize). Nothing in the profile depends on the size, and this path now
    // runs on every canvas box change — including each tick of the edit sheet opening — where
    // re-running it would reallocate the drawing buffer a second time for nothing.
    const dpr = Number.isFinite(window.devicePixelRatio) ? window.devicePixelRatio : 1;
    if (dpr !== this._lastAppliedDpr) {
      this._lastAppliedDpr = dpr;
      this.#applyRendererProfile(this.renderer);
    }
  }

  /**
   * Reserve `rightPx` of the canvas's right edge for the Studio panel (0 clears it). The orbiter
   * reframes into the remaining left region without deformation (see #applySize). Exposed on `window` for
   * the React Studio shell to call, mirroring how EW exposes `__EW_setEditViewport`.
   */
  setViewportInset(rightPx) {
    this._viewportInsetRight = Math.max(0, Math.round(Number(rightPx) || 0));
    this.#applySize();
  }

  /** The right-edge inset (px) reserved by the Studio panel. This controller is the only resize owner
   *  — nothing else sizes the renderer or the camera — so this is read, not mirrored, elsewhere. */
  get viewportInsetRight() {
    return this._viewportInsetRight;
  }

  #initFrameStats() {
    // One stats object, mutated in place by `#recordFrame` — also published on the window handle
    // so tooling can read it without us allocating a fresh object every frame.
    // Frame timing plus the renderer's own counters. The counters are what tell you whether a
    // cheaper-LOOKING scene actually released anything: draw calls falling while `geometries` and
    // `textures` stay flat means the work was hidden, not dropped.
    this._frameStats = {
      fps: 0,
      frameTimeMs: 0,
      sampleCount: 0,
      calls: 0,
      triangles: 0,
      geometries: 0,
      textures: 0,
      sceneProfile: this._sceneProfile,
    };
    if (typeof window !== 'undefined') {
      window.__orbitersFrameStats = this._frameStats;
    }
    if (!this._showFpsOverlay || typeof document === 'undefined') {
      return;
    }

    const overlay = document.createElement('div');
    overlay.setAttribute('data-role', 'fps-overlay');
    overlay.style.position = 'fixed';
    overlay.style.top = '12px';
    overlay.style.right = '12px';
    overlay.style.zIndex = '2000';
    overlay.style.pointerEvents = 'none';
    overlay.style.padding = '6px 8px';
    overlay.style.borderRadius = '8px';
    overlay.style.background = 'rgba(0, 0, 0, 0.55)';
    overlay.style.backdropFilter = 'blur(4px)';
    overlay.style.color = 'var(--color1, rgba(255,255,255,0.92))';
    overlay.style.border = '1px solid rgba(255,255,255,0.18)';
    overlay.style.fontFamily = 'var(--orbiters-font-family, monospace)';
    overlay.style.fontSize = '12px';
    overlay.style.lineHeight = '1.25';
    overlay.style.whiteSpace = 'pre';
    overlay.textContent = 'FPS --\nFT -- ms';
    document.body.appendChild(overlay);
    this._fpsOverlayEl = overlay;
    // Painted once here, because booting straight into the audio-first mode never TRANSITIONS into
    // it — the profile is already 'off' when this runs, and nothing else will ever repaint an overlay
    // that has no frames coming. Left as the placeholder, the readout would sit on "FPS --" and say
    // nothing about the one thing it exists to answer.
    this.#paintFpsOverlay();
  }

  #recordFrame(now) {
    if (!Number.isFinite(now)) {
      return;
    }

    if (this._lastFrameTimestamp == null) {
      this._lastFrameTimestamp = now;
      return;
    }

    const frameTimeMs = now - this._lastFrameTimestamp;
    this._lastFrameTimestamp = now;
    if (!Number.isFinite(frameTimeMs) || frameTimeMs <= 0) {
      return;
    }

    // Running sum: add the new sample, subtract the one that ages out — avoids a reduce over the
    // whole window every frame.
    this._fpsSampleWindow.push(frameTimeMs);
    this._fpsSampleTotal += frameTimeMs;
    if (this._fpsSampleWindow.length > this._fpsSampleWindowSize) {
      this._fpsSampleTotal -= this._fpsSampleWindow.shift();
    }

    const sampleCount = this._fpsSampleWindow.length;
    const avgFrameTimeMs = sampleCount > 0 ? this._fpsSampleTotal / sampleCount : 0;
    const avgFps = avgFrameTimeMs > 0 ? 1000 / avgFrameTimeMs : 0;

    if (this._frameStats) {
      this._frameStats.fps = avgFps;
      this._frameStats.frameTimeMs = avgFrameTimeMs;
      this._frameStats.sampleCount = sampleCount;
      // Read, never reset: `renderer.info.render` is per-frame and three.js resets it itself, while
      // `.memory` is a live count of what is currently allocated.
      const info = this.renderer?.info;
      if (info) {
        this._frameStats.calls = info.render?.calls ?? 0;
        this._frameStats.triangles = info.render?.triangles ?? 0;
        this._frameStats.geometries = info.memory?.geometries ?? 0;
        this._frameStats.textures = info.memory?.textures ?? 0;
      }
      this._frameStats.sceneProfile = this._sceneProfile;
    }

    if (now - this._fpsOverlayLastPaintAt < 250) {
      return;
    }
    this._fpsOverlayLastPaintAt = now;
    this.#paintFpsOverlay();
  }

  /**
   * Write the readout. Called on a throttle while drawing, and once when the 3D goes off or comes
   * back — in the off state nothing else would ever repaint it, and a readout frozen on the last
   * frame before the toggle would answer the overlay's whole question wrongly.
   */
  #paintFpsOverlay() {
    if (!this._fpsOverlayEl) return;
    const stats = this._frameStats;

    // With the 3D off the frame numbers are not "zero", they are absent — there is no context and no
    // loop to have them. Saying so is what makes the mode checkable on the machine: a black viewport
    // looks identical whether we stopped rendering or merely drew nothing, and only this tells you
    // which. GEO/TEX come from the renderer's own live allocation counts, so both reading zero is the
    // evidence that the scene was released rather than hidden.
    if (this.#is3dOff()) {
      this._fpsOverlayEl.textContent =
        `3D OFF\nCONTEXT none\nLOOP none\n` +
        `GEO ${stats?.geometries ?? 0}  TEX ${stats?.textures ?? 0}` +
        `${this._ownsRenderer ? '' : '\n(realm)'}`;
      return;
    }

    // `renderer.info.render` is reset per `render()` call, so in a shared realm the draw counts read
    // back as whichever voice drew last — not the frame. Showing them there would be a number that
    // looks like the realm's and is not. The memory counts ARE realm-wide and stay.
    const drawLine = this._ownsRenderer
      ? `CALLS ${stats?.calls ?? 0}  TRI ${stats?.triangles ?? 0}\n`
      : '';
    this._fpsOverlayEl.textContent =
      `FPS ${(stats?.fps ?? 0).toFixed(1)}\nFT ${(stats?.frameTimeMs ?? 0).toFixed(1)} ms\n` +
      drawLine +
      `GEO ${stats?.geometries ?? 0}  TEX ${stats?.textures ?? 0}\n` +
      `${this._sceneProfile.toUpperCase()}${this._ownsRenderer ? '' : ' (realm)'}`;
  }

  /**
   * Advance the frame clock: seconds-now plus a delta capped at 0.5s, so every
   * callback gets tab-inactivity clamping for free. One computation per frame,
   * one reused object.
   */
  #advanceFrame(nowMs) {
    const frame = this._frame;
    frame.nowSec = nowMs / 1000;
    const dtSec = this._frameLastNowMs === null ? 0 : (nowMs - this._frameLastNowMs) / 1000;
    frame.dtSec = dtSec > 0 ? Math.min(dtSec, 0.5) : 0;
    this._frameLastNowMs = nowMs;
    return frame;
  }

  #startLoop() {
    // Shared-renderer voices are driven by the compositor's loop, never their own.
    if (!this._ownsRenderer) {
      return;
    }
    // No renderer, no loop — and no rAF at all, not a loop that wakes each frame and returns. The
    // measurement that produced this mode came from a backgrounded tab, where the browser stops
    // calling rAF entirely; a loop that still ran and did nothing would be a different, weaker thing.
    if (!this.renderer) {
      return;
    }
    if (this._disposed || !this._renderActive || this._animationFrame != null) {
      return;
    }
    const tick = (now = (typeof performance !== 'undefined' ? performance.now() : Date.now())) => {
      if (this._disposed || !this._renderActive) {
        this._animationFrame = null;
        return;
      }
      this.#recordFrame(now);
      const frame = this.#advanceFrame(now);
      // removed: controls.update(); (controls are disabled)
      this.renderCallbacks.forEach((cb) => cb(frame));
      this.#draw(null);
      this._animationFrame = requestAnimationFrame(tick);
    };
    this._animationFrame = requestAnimationFrame(tick);
  }

  setRenderActive(active, { renderOnce = true } = {}) {
    const next = Boolean(active);
    if (this._disposed || this._renderActive === next) {
      return;
    }
    this._renderActive = next;
    // Reset the frame clock on pause so the first resumed frame reports dt 0
    // instead of the whole pause (both loop-owning and compositor-driven voices).
    if (!next) {
      this._frameLastNowMs = null;
    }
    // Shared-renderer voices have no own loop to start/stop: the compositor reads `renderActive` each
    // frame and skips paused voices. Just flip the flag.
    if (!this._ownsRenderer) {
      return;
    }
    if (!next) {
      if (this._animationFrame != null) {
        cancelAnimationFrame(this._animationFrame);
        this._animationFrame = null;
      }
      this._lastFrameTimestamp = null;
      return;
    }
    if (renderOnce) {
      try {
        // Through the pass, like every other frame — a resumed voice must not flash one
        // clean frame before the effect's picture comes back.
        this.#draw(null);
      } catch (_) {}
    }
    this.#startLoop();
  }

  /** Whether the compositor should render this voice this frame (false while suspended/disposed). */
  get renderActive() {
    return this._renderActive && !this._disposed;
  }

  /**
   * Shared-renderer mode only: draw ONE frame of this voice's scene into `rect` of the
   * compositor's shared renderer. The compositor owns the rAF loop and the rect geometry (device px,
   * GL bottom-left origin); this method runs the voice's per-frame callbacks then scissor-renders so
   * the draw is confined to its cell. No-op while paused or disposed. Single-orbiter never calls this.
   */
  renderViewport(rect) {
    if (this._disposed || !this._renderActive || !rect) {
      return;
    }
    // A realm in the audio-first mode has no compositor to call this at all; the guard is for the
    // toggle landing mid-frame, between the compositor reading its voice list and reaching this one.
    if (!this.renderer) {
      return;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // The compositor calls this once per realm frame, so the interval between calls IS the realm's
    // frame rate — which is the number worth having when several voices are playing at once.
    if (this._frameStats) {
      this.#recordFrame(now);
    }

    // No frame gate here. The realm's cadence has ONE owner and it is the compositor: it caps its own
    // loop, so this is already called at the capped rate and every call is a frame the realm decided
    // to draw. Gating again against a second clock — the compositor times its rAF timestamp, this
    // would time execution — only means occasionally refusing a frame the realm has already accepted,
    // which leaves the voice's scene un-updated while its cell is still drawn. That reads as stutter.
    //
    // The draw could not be skipped here anyway: the compositor clears the whole canvas and then
    // scissor-renders each voice into its cell, so a voice that declined to draw would leave a black
    // hole where its cell is.
    const frame = this.#advanceFrame(now);
    this.renderCallbacks.forEach((cb) => cb(frame));
    const r = this.renderer;
    r.setViewport(rect.x, rect.y, rect.width, rect.height);
    r.setScissor(rect.x, rect.y, rect.width, rect.height);
    r.setScissorTest(true);
    this.#draw(rect);
  }

  /**
   * The ONE draw. Straight to the framebuffer, or — when this voice carries a post
   * pass — through it: the scene goes into the pass's own target and comes back as a
   * full-screen quad drawn inside this voice's rect. The shared renderer's scissor is
   * still standing, so the quad lands in this voice's cell and nowhere else.
   *
   * @param {{x:number,y:number,width:number,height:number}|null} rect - The voice's
   *        cell in the shared realm; null when it owns the whole canvas.
   */
  #draw(rect) {
    // The 3D can go off between a caller deciding to draw and this running — `setRenderActive` draws
    // one frame on resume, and the toggle is a user gesture that lands whenever it lands.
    if (!this.renderer) return;
    if (this._postPass) {
      this._postPass.render(this.renderer, this.scene, this.camera, rect);
      return;
    }
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Give this voice a full-frame shader pass, or null to draw straight again. One
   * owner: the caller that sets it disposes it.
   *
   * @param {{ render(renderer: object, scene: object, camera: object, rect: object|null): void }|null} pass
   */
  setPostPass(pass) {
    this._postPass = pass ?? null;
  }

  /**
   * Shared-renderer mode only: size this voice's CAMERA to its cell so the planet keeps its
   * aspect (the shared renderer's drawing-buffer size is owned by the compositor, not this controller).
   * Mirrors #applySize's aspect math without touching renderer size. Call on layout change, not per-frame.
   */
  setViewportSize(width, height) {
    if (!width || !height) {
      return;
    }
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  attachOverlay(group) {
    if (!group || !group.isObject3D) {
      console.warn('[WorldSceneController] attachOverlay expects a THREE.Object3D');
      return;
    }
    if (!this.overlayGroups.has(group)) {
      this.overlayGroups.add(group);
      this.scene.add(group);
    }
  }

  detachOverlay(group) {
    if (this.overlayGroups.has(group)) {
      this.overlayGroups.delete(group);
      this.scene.remove(group);
    }
  }

  /**
   * Register a per-frame callback, run before render on both render paths
   * (own loop and compositor viewport). Each callback receives the frame clock
   * `{ nowSec, dtSec }` — dtSec is already clamped against tab-inactivity
   * jumps. The object is REUSED every frame: read it synchronously, never
   * retain or mutate it. Don't build a private clock in a callback.
   */
  addRenderCallback(cb) {
    if (typeof cb === 'function') {
      this.renderCallbacks.add(cb);
    }
  }

  removeRenderCallback(cb) {
    this.renderCallbacks.delete(cb);
  }

  updatePerformanceProfile(profile) {
    this.performanceProfile = this.#resolvePerformanceProfile(profile);
    // The profile is still recorded — this voice's own scene reads it — but the RENDERER knobs it
    // carries are realm-wide, and in a shared realm the renderer is the compositor's. Writing them
    // from here would have every voice re-clamp the pixel ratio and the shadow map for all its
    // siblings on any graphics-quality change, and would undo the realm's performance-mode clamp
    // behind the compositor's back.
    if (!this._ownsRenderer) return;
    this.#applyRendererProfile(this.renderer);
  }

  get sceneProfile() {
    return this._sceneProfile;
  }

  /**
   * Turn the 3D off or back on, live. Returns true when the profile actually changed, so the caller
   * knows whether to shed or rebuild the scene's contents.
   *
   * This method owns only what the CONTROLLER owns: the renderer and its context, the rAF loop, the
   * resize binding, the camera controls, and the post pass slot. Everything in the scene GRAPH
   * belongs to whoever built it — they react to the same preference and shed their own resources.
   *
   * Going OFF destroys the context rather than parking it. An idle context is not free: it holds its
   * drawing buffer, and in a realm it still costs a viewport per frame. Keeping one alive to make the
   * return trip cheap would preserve exactly the cost the mode exists to remove, so coming BACK is a
   * rebuild — the same work boot does. That is the deliberate trade, in the direction where a pause
   * is affordable.
   *
   * @param {'off'|'full'} profile
   * @param {{ sharedRenderer?: object }} [opts] a realm hands its voice back a renderer on the way on;
   *   the compositor owns it, so the voice borrows rather than creates.
   */
  setSceneProfile(profile, { sharedRenderer = null } = {}) {
    const next = profile === 'off' ? 'off' : 'full';
    if (this._disposed || next === this._sceneProfile) return false;
    this._sceneProfile = next;

    if (next === 'off') {
      // Release the SLOT, do not dispose the pass. `setPostPass` says the caller that sets it
      // disposes it, and that caller is the effect-visuals bridge, which is still holding the same
      // object and counting holders. Disposing it here would be a second owner freeing someone
      // else's render target. The bridge is torn down immediately after this on the same toggle, and
      // that is what actually frees it.
      this._postPass = null;
      this.#teardown3d();
      return true;
    }

    this.#build3d(sharedRenderer);
    return true;
  }

  /**
   * Give up the renderer, the loop and everything bound to them. The scene and camera survive — see
   * the constructor for why the consumers depend on that.
   */
  #teardown3d() {
    if (this._animationFrame != null) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = null;
    }
    this._lastFrameTimestamp = null;
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      this._onResize = null;
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this.controls && typeof this.controls.dispose === 'function') {
      this.controls.dispose();
    }
    this.controls = null;
    // Only an OWNED renderer is disposed. A voice in a realm borrows the compositor's, and disposing
    // it here would take its siblings' pixels with it — the realm drops its own compositor instead.
    if (this._ownsRenderer && this.renderer) {
      // Cleared BEFORE it is disposed, and this is not tidiness. A lone orbiter's canvas is the
      // page's own element and outlives every renderer built on it, so the last frame drawn into it
      // stays composited after the context is released — a fully lit planet sitting under a mode
      // whose entire claim is that nothing is being drawn. (A realm has no such problem: its
      // compositor owns its canvas and takes the element out of the DOM with it.)
      //
      // One clear, once, at the switch. The state is set explicitly rather than trusted: `alpha:true`
      // only makes the buffer CAPABLE of being transparent — what actually lands is the renderer's
      // current clear colour and alpha — and a scissor rect left over from the last frame would
      // otherwise clear a rectangle instead of the canvas. Both are being thrown away with the
      // renderer on the next line, so setting them costs nothing.
      this.renderer.setScissorTest(false);
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.clear();
      this.renderer.dispose();
    }
    this.renderer = null;
    this._sharedRenderer = null;
    this._lastAppliedDpr = null;
    if (this._frameStats) {
      this._frameStats.sceneProfile = this._sceneProfile;
      // Zeroed, not left at the last frame's values. A stale 30fps sitting in the readout for a mode
      // that draws nothing is worse than no readout — it is the exact question the overlay is there
      // to answer, answered wrongly.
      this._frameStats.fps = 0;
      this._frameStats.frameTimeMs = 0;
      this._frameStats.sampleCount = 0;
      this._frameStats.calls = 0;
      this._frameStats.triangles = 0;
      this._frameStats.geometries = 0;
      this._frameStats.textures = 0;
    }
    this._fpsSampleWindow = [];
    this._fpsSampleTotal = 0;
    this.#paintFpsOverlay();
  }

  /** Build the renderer, the loop and the bindings back up — the same work the constructor does. */
  #build3d(sharedRenderer) {
    // A realm's voice never makes a renderer. Its renderer is the compositor's, shared with every
    // sibling, and the realm hands it over when it has one. Left to fall through, this voice would
    // create a second context on the realm's canvas and start its own loop against it — one voice
    // quietly drawing over the whole realm.
    if (this._isRealmVoice && !sharedRenderer) {
      // Logged, because this is the one path where the controller ends up in a DIFFERENT state from
      // the one it was asked for, and it used to do so in silence. The preference then reads "3D on"
      // while this voice has no renderer at all, and everything downstream that builds a world has to
      // cope with that gap — `OrbitersPlayMode.activate` is where it is caught. Without a line here
      // the gap is invisible, and what you see instead is a card that failed to load for no stated
      // reason.
      console.warn(
        '[WorldSceneController] realm voice asked for the 3D with no compositor renderer — staying off.',
      );
      this._sceneProfile = 'off';
      return;
    }
    this._sharedRenderer = sharedRenderer || null;
    this._ownsRenderer = !this._isRealmVoice && !this._sharedRenderer;
    this.renderer = this._ownsRenderer ? this.#createRenderer(this.canvas) : this._sharedRenderer;
    this.controls = this.#createControls();
    if (this._frameStats) {
      this._frameStats.sceneProfile = this._sceneProfile;
    }
    this.#paintFpsOverlay();
    if (!this._ownsRenderer) {
      // The realm owns the loop, the canvas size and the renderer knobs for every voice at once. A
      // voice reaching for any of them would be deciding for its siblings.
      return;
    }
    this.#applySize();
    this.#bindResize();
    this.#startLoop();
  }

  dispose() {
    this._disposed = true;
    this._renderActive = false;
    cancelAnimationFrame(this._animationFrame);
    this._animationFrame = null;
    this._lastFrameTimestamp = null;
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this.controls && typeof this.controls.dispose === 'function') {
      this.controls.dispose();
    }
    this.renderCallbacks.clear();
    this.overlayGroups.clear();

    // A shared-renderer voice borrows the compositor's renderer + the realm's global slots — it must
    // NOT dispose the renderer or delete slots it never created (that would kill its siblings).
    if (this._ownsRenderer) {
      // Absent in the audio-first mode, where teardown already gave it up.
      this.renderer?.dispose();
      delete window.__orbitersSetViewportInset;
      delete window.__orbitersRefitViewport;
      if (window.__orbitersFrameStats) {
        delete window.__orbitersFrameStats;
      }
    }

    // Hand the realm's measurement back, so the next voice can take it. Without this a room whose
    // first tile is removed loses its readout for the rest of the session.
    if (sharedFrameStatsOwner === this) {
      sharedFrameStatsOwner = null;
      if (typeof window !== 'undefined' && window.__orbitersFrameStats === this._frameStats) {
        delete window.__orbitersFrameStats;
      }
    }
    this._frameStats = null;
    this._fpsSampleWindow = [];
    this._fpsSampleTotal = 0;
    if (this._fpsOverlayEl?.parentElement) {
      this._fpsOverlayEl.parentElement.removeChild(this._fpsOverlayEl);
    }
    this._fpsOverlayEl = null;
  }
}
